import React, { useMemo, useEffect } from 'react';
import { ProjectedRiskMetricsWidget } from './ProjectedRiskMetricsWidget';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
} from '~/atoms/dashboardAtom';
import { GET_RISK_METRICS_COMPARISON } from '~/graphql/queries/portfolio';
import { isNullEmptyOrUndefined } from '~/utils/validation';

// Helper function to check if value is valid for conversion
const isValidValue = (value: string | number | null | undefined): boolean => {
  return (
    !isNullEmptyOrUndefined(value) &&
    (typeof value === 'string' || typeof value === 'number')
  );
};

// Helper function to convert and format value
const formatValue = (
  value: string | number | null | undefined,
  decimals: number,
): string => {
  if (!isValidValue(value)) return 'N/A';
  const numValue = typeof value === 'number' ? value : Number(value);
  return isNaN(numValue) ? 'N/A' : numValue.toFixed(decimals);
};

interface RiskComparison {
  current: {
    var_30d_95?: string | number | null;
    sharpe_ratio?: string | number | null;
    volatility?: string | number | null;
  };
  proposed: {
    var_30d_95?: string | number | null;
    sharpe_ratio?: string | number | null;
    volatility?: string | number | null;
  };
}

interface RiskMetricsResponse {
  EQPortfoliosAnalysis: {
    analysis_data: {
      risk_comparison: RiskComparison;
    };
  }[];
}

export interface ProjectedRiskMetricsWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const ProjectedRiskMetricsWidgetWrapper: React.FC<
  ProjectedRiskMetricsWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  // Get the latest snapshot ID from the atom
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);

  // Use the latest snapshot ID or fallback to the hardcoded one
  const snapshotId = latestSnapshotId;

  const { data, loading, error, refetch } = useQuery<RiskMetricsResponse>(
    GET_RISK_METRICS_COMPARISON,
    {
      variables: { snapshotId },
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId, // Skip query if no snapshot ID is available
    },
  );

  // Automatically refetch when snapshotId changes
  useEffect(() => {
    if (snapshotId) {
      refetch();
    }
  }, [snapshotId, refetch]);

  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  const riskMetricsData = useMemo(() => {
    if (
      loading ||
      !data?.EQPortfoliosAnalysis?.[0]?.analysis_data?.risk_comparison
        ?.current ||
      !data?.EQPortfoliosAnalysis?.[0]?.analysis_data?.risk_comparison?.proposed
    ) {
      return [];
    }

    const comparison =
      data.EQPortfoliosAnalysis[0].analysis_data.risk_comparison;

    return [
      {
        label: 'VaR (30d, 95%)',
        currentValue: formatValue(comparison.current?.var_30d_95, 1),
        projectedValue: formatValue(comparison.proposed?.var_30d_95, 1),
        isPositiveChange:
          isValidValue(comparison.proposed?.var_30d_95) &&
          isValidValue(comparison.current?.var_30d_95)
            ? Number(comparison.proposed.var_30d_95) <
              Number(comparison.current.var_30d_95)
            : false,
      },
      {
        label: 'Sharpe Ratio',
        currentValue: formatValue(comparison.current?.sharpe_ratio, 2),
        projectedValue: formatValue(comparison.proposed?.sharpe_ratio, 2),
        isPositiveChange:
          isValidValue(comparison.proposed?.sharpe_ratio) &&
          isValidValue(comparison.current?.sharpe_ratio)
            ? Number(comparison.proposed.sharpe_ratio) >
              Number(comparison.current.sharpe_ratio)
            : false,
      },
      {
        label: 'Expected Volatility',
        currentValue: formatValue(comparison.current?.volatility, 1),
        projectedValue: formatValue(comparison.proposed?.volatility, 1),
        isPositiveChange:
          isValidValue(comparison.current?.volatility) &&
          isValidValue(comparison.proposed?.volatility)
            ? Number(comparison.current.volatility) >
              Number(comparison.proposed.volatility)
            : false,
      },
    ];
  }, [data, loading]);

  return (
    <ProjectedRiskMetricsWidget
      data={riskMetricsData}
      isLoading={loading}
      error={error || null}
      refetch={refetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      title={shortDescription}
      snapshotId={snapshotId}
    />
  );
};
